// API configuration
import { parseJwtPayload } from '../utils/jwtUtils';

type GlobalConfig = {
  API_ORIGIN_URL?: string;
  WEBSOCKET_URL?: string;
};

declare global {
  interface Window {
    __CONFIG__?: GlobalConfig;
  }
}

const safelyReadLocalStorage = (key: string): string | undefined => {
  try {
    return window.localStorage.getItem(key) || undefined;
  } catch (_) {
    return undefined;
  }
};

export const getConfiguredOriginUrl = (): string | undefined => {
  const fromWindow = window.__CONFIG__?.API_ORIGIN_URL;
  console.log('🔍 [API_CONFIG] window.__CONFIG__.API_ORIGIN_URL:', fromWindow);
  
  const fromLocalStorage = safelyReadLocalStorage('API_ORIGIN_URL');
  console.log('🔍 [API_CONFIG] localStorage.API_ORIGIN_URL:', fromLocalStorage);
  
  // Prioritize window config over localStorage in production
  if (fromWindow) {
    console.log('✅ [API_CONFIG] Using window config:', fromWindow);
    return ensureProtocol(fromWindow);
  }

  if (fromLocalStorage) {
    console.log('⚠️ [API_CONFIG] Using localStorage config:', fromLocalStorage);
    return ensureProtocol(fromLocalStorage);
  }

  const fromVite = typeof import.meta !== 'undefined' && (import.meta as any).env
    ? ((import.meta as any).env.VITE_API_ORIGIN_URL as string | undefined)
    : undefined;
  if (fromVite) return ensureProtocol(fromVite);

  return undefined;
};

// Helper function to ensure URL has a protocol
const ensureProtocol = (url: string | undefined): string | undefined => {
  if (!url) return undefined;
  
  // If it already has a protocol, return as-is
  if (url.startsWith('http://') || url.startsWith('https://')) {
    return url;
  }
  
  // If it's localhost, use http
  if (url.includes('localhost') || url.includes('127.0.0.1')) {
    return `http://${url}`;
  }
  
  // For all other cases, assume https
  return `https://${url}`;
};

const getConfiguredWebsocketUrl = (): string | undefined => {
  const fromWindow = window.__CONFIG__?.WEBSOCKET_URL;
  if (fromWindow) return normalizeWebsocketUrl(ensureWebsocketProtocol(fromWindow));

  const fromLocalStorage = safelyReadLocalStorage('WEBSOCKET_URL');
  if (fromLocalStorage) return normalizeWebsocketUrl(ensureWebsocketProtocol(fromLocalStorage));

  const fromVite = typeof import.meta !== 'undefined' && (import.meta as any).env
    ? ((import.meta as any).env.VITE_WEBSOCKET_URL as string | undefined)
    : undefined;
  if (fromVite) return normalizeWebsocketUrl(ensureWebsocketProtocol(fromVite));

  return undefined;
};

// Helper function to ensure WebSocket URL has a protocol
const ensureWebsocketProtocol = (url: string | undefined): string | undefined => {
  if (!url) return undefined;
  
  // If it already has a WebSocket protocol, return as-is
  if (url.startsWith('ws://') || url.startsWith('wss://')) {
    return url;
  }
  
  // If it's localhost, use ws
  if (url.includes('localhost') || url.includes('127.0.0.1')) {
    return `ws://${url}`;
  }
  
  // For all other cases, assume wss (secure)
  return `wss://${url}`;
};

// Known River hosts and the WebSocket path Hocuspocus is actually mounted
// on behind the ALB. Every `getWebsocketUrl()` branch below must agree with
// this table — it exists so an incorrectly-injected override (deploy-time
// `window.__CONFIG__.WEBSOCKET_URL`, a stale localStorage value, etc.) can be
// corrected instead of silently routing every WebSocket connection at the
// bare API origin, which the ALB sends to FastAPI (not Hocuspocus) and
// breaks all realtime collaboration. See incident: WEBSOCKET_URL shipped
// without "/ws" in deploy-webapp.yml.
const KNOWN_WS_HOST_PATHS: ReadonlyArray<{ host: string; path: string }> = [
  { host: 'api.rivereditor.com', path: '/ws' },
  { host: 'app.riversystem.ai', path: '/websocket' },
];

const findKnownWsHostRule = (hostname: string) =>
  KNOWN_WS_HOST_PATHS.find((rule) => hostname === rule.host || hostname.endsWith(`.${rule.host}`));

// Converges a configured WebSocket URL override onto its canonical path when
// the host is a known River deployment target and the override is missing a
// path (i.e. it points at the bare origin). Unknown hosts (self-hosted,
// local dev overrides, etc.) are passed through untouched.
const normalizeWebsocketUrl = (url: string | undefined): string | undefined => {
  if (!url) return url;
  const trimmed = url.replace(/\/+$/, '');
  try {
    const parsed = new URL(trimmed);
    const hasPath = Boolean(parsed.pathname) && parsed.pathname !== '/';
    if (!hasPath) {
      const rule = findKnownWsHostRule(parsed.hostname);
      if (rule) {
        console.warn(
          `⚠️ [CONFIG] WEBSOCKET_URL override "${url}" is missing the required path for ` +
          `"${parsed.hostname}" — correcting to "${trimmed}${rule.path}"`
        );
        return `${trimmed}${rule.path}`;
      }
    }
  } catch (_) {
    // Not a parseable absolute URL — return as configured.
  }
  return trimmed;
};

// Loud-at-boot safety net: even after normalization above, if the final
// WEBSOCKET_URL is missing its required path on a known River host, fail
// loudly instead of letting every realtime feature silently break.
const assertWebsocketUrlIsSane = (wsUrl: string): void => {
  try {
    const parsed = new URL(wsUrl);
    const hasPath = Boolean(parsed.pathname) && parsed.pathname !== '/';
    const rule = findKnownWsHostRule(parsed.hostname);
    if (rule && !hasPath) {
      console.error(
        `🚨 [CONFIG] FATAL: WEBSOCKET_URL "${wsUrl}" is missing the required "${rule.path}" ` +
        `path for host "${parsed.hostname}". All WebSocket/CRDT collaboration will fail. ` +
        'Check window.__CONFIG__.WEBSOCKET_URL injection (deploy-webapp.yml) or a stale ' +
        'localStorage["WEBSOCKET_URL"] override.'
      );
    }
  } catch (_) {
    // Not a parseable absolute URL — nothing to assert.
  }
};

const getOriginUrl = () => {
  // Hostname-based environment detection - immediate and reliable
  const hostname = window.location.hostname;
  const protocol = window.location.protocol;
  
  // Selfhosted environment
  if (hostname === 'app.riversystem.ai' || hostname === 'riversystem.ai' || hostname.endsWith('.riversystem.ai')) {
    console.log('✅ [CONFIG] Selfhosted environment detected');
    return 'https://app.riversystem.ai';
  }
  
  // Production environment
  if (hostname === 'rivereditor.com' || hostname.endsWith('.rivereditor.com')) {
    console.log('✅ [CONFIG] Production environment detected');
    return 'https://api.rivereditor.com';  // Direct to ALB (bypasses CloudFront 60s limit)
  }
  
  // Local development
  if (hostname === 'localhost' || hostname === '127.0.0.1') {
    console.log('✅ [CONFIG] Development environment detected');
    return 'http://localhost:5001';
  }
  
  // File protocol (Electron or local file)
  if (protocol === 'file:') {
    console.log('✅ [CONFIG] File protocol detected, using localhost');
    return 'http://localhost:5001';
  }
  
  // Unknown environment - default to current origin
  console.log('⚠️ [CONFIG] Unknown environment, using current origin:', window.location.origin);
  return window.location.origin;
};

const getWebsocketUrl = () => {
  // Hostname-based WebSocket URL detection
  const hostname = window.location.hostname;
  const protocol = window.location.protocol;
  
  console.log('🔍 [CONFIG] Detecting WebSocket URL from hostname:', hostname);
  
  // Selfhosted environment
  if (hostname === 'app.riversystem.ai' || hostname === 'riversystem.ai' || hostname.endsWith('.riversystem.ai')) {
    console.log('✅ [CONFIG] Selfhosted WebSocket: wss://app.riversystem.ai/websocket');
    return 'wss://app.riversystem.ai/websocket';
  }
  
  // Production environment
  if (hostname === 'rivereditor.com' || hostname.endsWith('.rivereditor.com')) {
    console.log('✅ [CONFIG] Production WebSocket: wss://api.rivereditor.com/ws');
    return 'wss://api.rivereditor.com/ws';  // Direct to ALB (bypasses CloudFront)
  }
  
  // Local development - Hocuspocus WebSocket server on port 1235
  if (hostname === 'localhost' || hostname === '127.0.0.1') {
    console.log('✅ [CONFIG] Development WebSocket: ws://localhost:1235');
    return 'ws://localhost:1235';
  }
  
  // File protocol (Electron)
  if (protocol === 'file:') {
    console.log('✅ [CONFIG] File protocol WebSocket: ws://localhost:1235');
    return 'ws://localhost:1235';
  }
  
  // Unknown environment - use current host with appropriate protocol
  const wsProtocol = protocol === 'https:' ? 'wss' : 'ws';
  const wsUrl = `${wsProtocol}://${hostname}/ws`;
  console.log('⚠️ [CONFIG] Unknown environment WebSocket:', wsUrl);
  return wsUrl;
};

// Prefer an explicit override (window.__CONFIG__ / localStorage / VITE_*) when
// present — this is what lets a non-browser host (e.g. the Tauri Notes app
// served from tauri://localhost) point at a real backend. Falls back to the
// existing hostname-based detection so the hosted web app is unaffected.
export const API_ORIGIN_URL = getConfiguredOriginUrl() ?? getOriginUrl();
export const API_BASE_URL = `${API_ORIGIN_URL}/api`;
export const WEBSOCKET_URL = getConfiguredWebsocketUrl() ?? getWebsocketUrl();
assertWebsocketUrlIsSane(WEBSOCKET_URL);

/**
 * The raw token sent to the WebSocket server on every (re)connect. This is
 * the ONLY token the server ever sees for WS auth, so it is also the only
 * correct source for `getWsIdentitySub()` below — never `id_token` or any
 * other localStorage value, which can diverge from this one after Auth0
 * account linking.
 */
function getWsAuthToken(): string | null {
  try {
    return localStorage.getItem('authToken');
  } catch (_) {
    // localStorage may be unavailable (e.g. iframe sandbox)
    return null;
  }
}

/**
 * Returns the `params` object for the WebsocketProvider that includes the
 * current auth token.  Passed as `params` to HocuspocusWebsocketProvider;
 * the shim forwards the token via the Hocuspocus handshake auth message
 * (not as a URL query param, but the server still reads requestParameters
 * for backward compat with y-websocket URL-param style).
 */
export function getWsAuthParams(): Record<string, string> {
  const token = getWsAuthToken();
  return token ? { token } : {};
}

/**
 * The single source of truth for "which userId does the WebSocket server
 * see me as" — parses the `sub` claim out of the exact same token
 * `getWsAuthParams()` sends. Per-user room names (`space:{id}:{userId}`,
 * `notification:{userId}`, `space-chat:{id}:{userId}`, etc.) MUST be built
 * from this function, not `useAuth().user.sub` or `getCurrentUserId()` —
 * both of those can read `id_token` or the Auth0 SDK's cached user, either
 * of which can carry a different `sub` than the access token actually sent
 * to the WS server (e.g. after Auth0 account linking), causing the server
 * to permanently reject the room and the client to reconnect-loop forever.
 */
export function getWsIdentitySub(): string | null {
  const token = getWsAuthToken();
  if (!token) return null;
  try {
    return parseJwtPayload(token)?.sub ?? null;
  } catch (_) {
    return null;
  }
}

// Debug logging
console.log('🎯 [API_CONFIG] Final configuration:', {
  API_ORIGIN_URL,
  API_BASE_URL,
  WEBSOCKET_URL,
  hostname: window.location.hostname,
  protocol: window.location.protocol
});

